import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { motion } from 'framer-motion'
import {
  Eye, EyeOff, Mail, Lock,
  ArrowRight, Check, Loader2,
} from 'lucide-react'
import { toast } from 'sonner'

import { getLoginToken } from '@/redux/Auth/actions'
import { useAuth } from '@/config/AuthContext'

const SHIPEZ_LOGO_URL =
  'https://customer-assets.emergentagent.com/job_c083c188-1797-4d1e-a073-4159fcae87e4/artifacts/z4ndtxat_shipez-logo-new.webp'

const LOCKER_FREIGHT_LOGO_URL =
  'https://api.shipez.ai/UploadedFile/CompanyProfile/Locker%20Freight/LockerFreight-logo.png'

function isLockerFreightPath() {
  const pathname = window.location?.pathname || ''
  return pathname === '/locker-freight/login' || pathname.includes('locker-freight')
}

export default function Login2({ onNavigate }) {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { setIsAuthenticated } = useAuth()

  const [showPassword, setShowPassword] = useState(false)
  const [remember, setRemember] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)


  const onLockerFreightRoute = isLockerFreightPath()
  const [isLockerFreight] = useState(onLockerFreightRoute)
  const [customLogo, setCustomLogo] = useState(
    onLockerFreightRoute ? LOCKER_FREIGHT_LOGO_URL : SHIPEZ_LOGO_URL
  )

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    const payload = JSON.stringify({
      username: email,
      password: password,
    })

    try {
      const authToken = await dispatch(getLoginToken(payload))
      if (authToken) {
        toast.success('Login successful!')
        setIsAuthenticated(true)
        if (onNavigate) {
          onNavigate('/import-booking')
        } else {
          navigate('/import-booking')
        }
      }
    } catch (error) {
      console.log(error)
      const errorMessage =
        typeof error === 'string'
          ? error
          : error?.message || error?.response?.data?.detail || 'Login failed'
      toast.error(errorMessage)
    }
    setLoading(false)
  }

  return (
    <div className="min-h-screen flex bg-white overflow-hidden text-white">
      {/* ════════════ LEFT — Video hero ════════════ */}
      <div className="relative hidden lg:flex lg:w-[70%] min-h-screen flex-col overflow-hidden">
        {/* Background video */}
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 h-full w-full object-cover"
        >
          <source src="/Container.mp4" type="video/mp4" />
        </video>

        {/* Subtle dark overlay on video */}
        <div className="pointer-events-none absolute inset-0 z-[1] bg-black/32" aria-hidden />

        {/* Subtle vignette for text legibility */}
        <div
          className="pointer-events-none absolute inset-0 z-[2]"
          style={{
            background:
              'radial-gradient(ellipse at center, transparent 35%, rgba(0,0,0,0.55) 100%)',
          }}
        />

        {/* Content — centered vertically & horizontally */}
        <div className="relative z-10 flex min-h-0 w-full flex-1 flex-col items-center justify-center px-8 py-12 xl:px-16">
          {/* Hero text */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="max-w-2xl text-center [text-shadow:0_2px_24px_rgba(0,0,0,0.55)]"
          >
            <h1 className="text-[42px] xl:text-[54px] font-semibold leading-[1.08] tracking-tight text-white">
              Digital Logistics System
            </h1>
            <p className="mt-5 mx-auto max-w-2xl text-center text-base xl:text-lg font-light leading-relaxed tracking-wide text-white">
              Effectively monitor and control your entire logistics
            </p>
          </motion.div>
        </div>
      </div>

      {/* ════════════ RIGHT — Sign In form ════════════ */}
      <div className="relative flex min-h-screen w-full lg:w-[30%] flex-col border-l border-slate-200/90 bg-white px-6 py-12 lg:px-6 xl:px-8">
        <div className="flex flex-1 flex-col items-center justify-center">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="relative z-10 w-full max-w-sm text-slate-900"
          >
            {/* Logo (dynamic — Shipez by default, Locker Freight on
              /locker-freight/login if that brand's logo is reachable) */}
            <div className="text-center">
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.25 }}
                className="inline-flex items-center justify-center px-2 py-1"
              >
                <img
                  src={customLogo}
                  alt={isLockerFreight ? 'Locker Freight Logo' : 'Shipez Logo'}
                  className="h-[52px] w-auto max-w-[260px] object-contain sm:h-14 xl:h-[60px] xl:max-w-[280px]"
                  draggable={false}
                  onError={() => {
                    // If the brand logo can't load (404, network, wrong path,
                    // etc.) drop back to the Shipez logo so we never display
                    // a broken image.
                    if (customLogo !== SHIPEZ_LOGO_URL) {
                      setCustomLogo(SHIPEZ_LOGO_URL)
                    }
                  }}
                />
              </motion.div>
              <p className="mt-3 text-sm text-slate-500">
                Welcome back! Please enter your details to continue.
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="mt-8 space-y-4">
              {/* Email */}
              <div>
                <label className="mb-1.5 block text-[13px] font-semibold text-slate-800">
                  Email Address
                </label>
                <div className="group relative">
                  <Mail
                    className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 transition-colors group-focus-within:text-blue-600"
                  />
                  <input
                    type="text"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your username"
                    required
                    className="h-12 w-full rounded-xl border border-slate-200 bg-slate-50 pl-11 pr-3 text-sm text-slate-900 placeholder:text-slate-400 shadow-[0_0_0_4px_rgba(59,130,246,0)] outline-none transition-[border-color,background-color,box-shadow] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] focus:border-blue-400/90 focus:bg-white focus:shadow-[0_0_0_4px_rgba(59,130,246,0.16)]"
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label className="mb-1.5 block text-[13px] font-semibold text-slate-800">
                  Password
                </label>
                <div className="group relative">
                  <Lock
                    className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 transition-colors group-focus-within:text-blue-600"
                  />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    required
                    className="h-12 w-full rounded-xl border border-slate-200 bg-slate-50 pl-11 pr-11 text-sm text-slate-900 placeholder:text-slate-400 shadow-[0_0_0_4px_rgba(59,130,246,0)] outline-none transition-[border-color,background-color,box-shadow] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] focus:border-blue-400/90 focus:bg-white focus:shadow-[0_0_0_4px_rgba(59,130,246,0.16)]"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 transition-colors hover:text-slate-800"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {/* Remember me */}
              <div className="flex items-center pt-1">
                <label className="flex cursor-pointer select-none items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setRemember((v) => !v)}
                    aria-pressed={remember}
                    className={[
                      'flex h-4 w-4 items-center justify-center rounded border transition-all',
                      remember
                        ? 'border-blue-500 bg-gradient-to-br from-blue-600 to-cyan-500 shadow-[0_0_0_3px_rgba(59,130,246,0.2)]'
                        : 'border-slate-300 bg-white hover:border-slate-400',
                    ].join(' ')}
                  >
                    {remember && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                  </button>
                  <span className="text-[13px] text-slate-600">Remember me</span>
                </label>
              </div>

              {/* Submit button */}
              <button
                type="submit"
                disabled={loading}
                className="group relative mt-2 h-12 w-full overflow-hidden rounded-xl text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-[1px] disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:translate-y-0"
                style={{
                  background:
                    'linear-gradient(90deg, #2563eb 0%, #0ea5e9 50%, #22d3ee 100%)',
                  boxShadow:
                    '0 12px 30px -10px rgba(37,99,235,0.55), 0 6px 16px -6px rgba(34,211,238,0.35)',
                }}
              >
                <span
                  className="absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                  style={{
                    background:
                      'linear-gradient(90deg, #22d3ee 0%, #0ea5e9 50%, #2563eb 100%)',
                  }}
                />
                <span className="relative inline-flex items-center justify-center gap-2">
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    <>
                      Sign In
                      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </>
                  )}
                </span>
              </button>
            </form>
          </motion.div>
        </div>

        <div className="mt-auto flex shrink-0 items-center justify-center gap-2.5 border-t border-slate-100 pt-5 pb-1">
          <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">
            Powered By
          </span>
          <img
            src="/poweredlogored.png"
            alt="Info-x"
            className="h-auto w-auto object-contain opacity-90"
            draggable={false}
          />
        </div>
      </div>
    </div>
  )
}
